import { InteractionRequiredAuthError, type IPublicClientApplication } from '@azure/msal-browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { publicClient } from '@/api/publicClient';
import type { components } from '@/api/generated/schema';
import { apiTokenRequest } from '@/auth/config';
import { currentUserQueryKey } from '@/auth/useCurrentUser';
import { clearPendingInvite } from '@/auth/pendingInvite';

export type PublicInvite = components['schemas']['PublicInviteResponse'];

/**
 * A 404 from any invite route: malformed, unknown, expired, used, revoked or
 * replaced by a resend. The API answers every one of those the same way on
 * purpose (so a probe learns nothing), and the page does too — one message,
 * never a guess at which it was.
 */
export class InviteGoneError extends Error {
  constructor() {
    super('This invitation link is no longer valid');
    this.name = 'InviteGoneError';
  }
}

/**
 * The API refused in a sentence of its own, shown as it comes: the 409s
 * (`PublicInviteEndpoints` — a different email, already a member, a federated
 * sign-in, no email claim, an address too long to be a sign-in name) and the
 * 503's ProblemDetails `detail` (the directory is not set up, or Graph failed).
 */
export class InviteRefusedError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'InviteRefusedError';
  }
}

/** Entra needs the person again (an expired session): the page sends them back through sign-in. */
export class InviteSignInNeededError extends Error {
  constructor() {
    super('Sign-in is needed to accept the invitation');
    this.name = 'InviteSignInNeededError';
  }
}

/**
 * The accept's 403, which has no body: tenant resolution refuses a sign-in
 * that already belongs to a user who is not active — in practice someone
 * deactivated in another company (an Invited user has no sign-in linked).
 * Trying again can never help, so the page says so in its own words and
 * offers another sign-in, like the 409s.
 */
export class InviteSignInInactiveError extends Error {
  constructor() {
    super('This sign-in belongs to a user who is not active');
    this.name = 'InviteSignInInactiveError';
  }
}

function refusal(status: number, error: unknown, what: string): Error {
  if (status === 404) return new InviteGoneError();
  if (typeof error === 'string' && error !== '') return new InviteRefusedError(error, status);
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === 'string' && detail !== '') return new InviteRefusedError(detail, status);
  return new Error(`${what} failed: ${status}`);
}

/**
 * The company and the role, read through the link with no sign-in — through
 * `publicClient`, never `apiClient`: no bearer token for an office user who
 * opens the link to check it, and no 401 that could send anyone to the
 * office's sign-in page.
 *
 * Not refetched on focus (the person is coming back from their mailbox with a
 * code, and the page must not change under them), and no retry on a 404.
 */
export function usePublicInvite(token: string | undefined) {
  return useQuery({
    enabled: token !== undefined,
    queryKey: ['public-invite', token] as const,
    queryFn: async () => {
      const { data, response } = await publicClient.GET('/api/v1/public/invites/{token}', {
        params: { path: { token: token! } },
      });
      if (response.status === 404) throw new InviteGoneError();
      if (!response.ok || !data) throw new Error(`GET invite failed: ${response.status}`);
      return data;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * "Continue": makes sure the invited address has a sign-in (Graph creates one
 * if not). 204 either way — the API never says which.
 */
export function usePrepareInviteSignIn(token: string | undefined) {
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await publicClient.POST('/api/v1/public/invites/{token}/sign-in', {
        params: { path: { token: token! } },
      });
      if (!response.ok) throw refusal(response.status, error, 'Preparing sign-in');
    },
  });
}

/**
 * Links the signed-in person to the invitation.
 *
 * ⚠️ `publicClient` with the bearer token attached HERE, not `apiClient`.
 * `apiClient` reports every 401 to `MaintenanceGate`, which clears MSAL and
 * sends the tab to `/signin` — an invitee whose token was refused would lose
 * the page that explains what happened. And `getApiAccessToken`'s fallback is
 * `acquireTokenRedirect`, which would come back to the root without the
 * invite's state; an interaction-required error is handed to the page
 * instead, which signs in again the invite's way.
 */
export function useAcceptInvite(token: string | undefined, instance: IPublicClientApplication) {
  const queryClient = useQueryClient();
  return useMutation({
    // `afterSignIn`: started by the return from Entra. The page reads it back
    // (`variables`) so that an interaction-required error straight after a
    // fresh sign-in is shown rather than answered with another redirect.
    mutationFn: async (_options: { afterSignIn?: boolean }) => {
      const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!account) throw new InviteSignInNeededError();
      let accessToken: string;
      try {
        accessToken = (await instance.acquireTokenSilent({ ...apiTokenRequest, account })).accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) throw new InviteSignInNeededError();
        throw error;
      }
      const { data, error, response } = await publicClient.POST('/api/v1/public/invites/{token}/accept', {
        params: { path: { token: token! } },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.status === 403) throw new InviteSignInInactiveError();
      if (!response.ok || !data) throw refusal(response.status, error, 'Accepting the invitation');
      return data;
    },
    onSuccess: () => {
      // Done with the link: nothing to resume on a reload.
      clearPendingInvite();
      // This sign-in has a user row now. Anything that asked before (a 403)
      // must ask again rather than wait out /me's five-minute staleTime.
      void queryClient.invalidateQueries({ queryKey: currentUserQueryKey });
    },
  });
}
